import { useEffect } from 'react'

import { trackClientEvent } from 'analytics/analytics'
import {
  getMetasearchPriceAccuracyEvent,
  type MetasearchPriceAccuracyEventData,
} from 'analytics/metasearchPriceAccuracyTracking'

// Keys that have already fired, held at module scope (client-only, set inside an effect) so the
// event fires once per landing even when the tracking component mounts in more than one place.
// `BedbankRoomOptionsMobile`, for example, renders both on the page and inside the package-selection
// modal; a per-instance ref would let each mount fire its own event.
const firedDedupeKeys = new Set<string>()

/**
 * Fires the `metasearch_price_accuracy` client event once per `dedupeKey`, as soon as `ready` is
 * true and `eventData` is available. Shared by the legacy bedbank and SPP trackers so the once-only
 * firing side effect lives in a single place. Callers own deriving the price/subject (`eventData`),
 * the readiness gate (`ready`), and a stable `dedupeKey` (e.g. `pageType:offerId`) for their page.
 */
export default function useFireMetasearchPriceAccuracyEvent(
  eventData: MetasearchPriceAccuracyEventData | undefined,
  ready: boolean,
  dedupeKey: string,
) {
  useEffect(() => {
    if (!ready || !eventData || firedDedupeKeys.has(dedupeKey)) {
      return
    }

    firedDedupeKeys.add(dedupeKey)
    trackClientEvent(getMetasearchPriceAccuracyEvent(eventData))
  }, [ready, eventData, dedupeKey])
}
